/**
 * Comentários sobre um trecho de texto de uma ficha do mundo — a sugestão de mudança
 * que não é edição da ficha em si.
 *
 * <p>Uma consulta por ficha, não por campo: a região tem oito campos de texto, e pedir
 * um comentário por campo seria oito idas ao servidor para desenhar uma tela só. A
 * chave do react-query dedup as chamadas de todo campo `area` da mesma ficha — cada um
 * usa {@link useComentariosDoMundo}, e só a primeira monta a requisição.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../../api/cliente';
import type { Colecao } from './tipos';

const PROJETO = 'mistik-island';
const BASE = `/api/projetos/${PROJETO}/mundo`;

export interface ComentarioDoTexto {
  id: number;
  colecao: string;
  fichaId: number;
  campo: string;
  trecho: string;
  texto: string;
  autorId: number | null;
  /** Nulo é quem saiu do time — a tela escreve "usuário removido". */
  autorNome: string | null;
  autorHandle: string | null;
  autorCor: string | null;
  autorAvatar: string | null;
  criadoEm: string;
  /** Nulo enquanto ninguém marcou como resolvido. */
  resolvidoEm: string | null;
  resolvidoPorNome: string | null;
  /** Se quem está lendo pode apagar este comentário. */
  meu: boolean;
}

export const chaveComentariosDoMundo = (colecao: Colecao, fichaId: number) =>
  ['mundo-comentarios', colecao, fichaId] as const;

export function useComentariosDoMundo(colecao: Colecao, fichaId: number) {
  return useQuery({
    queryKey: chaveComentariosDoMundo(colecao, fichaId),
    queryFn: () => api<ComentarioDoTexto[]>(`${BASE}/${colecao}/${fichaId}/comentarios`),
    // A ficha ainda não existe (tela de criação) — sem id não há onde comentar.
    enabled: Number.isFinite(fichaId) && fichaId > 0,
  });
}

export function useMutacoesComentarioDoMundo(colecao: Colecao, fichaId: number) {
  const fila = useQueryClient();
  const recarregar = () =>
    void fila.invalidateQueries({ queryKey: chaveComentariosDoMundo(colecao, fichaId) });

  const criar = useMutation({
    mutationFn: (corpo: { campo: string; trecho: string; texto: string }) =>
      api<ComentarioDoTexto>(`${BASE}/${colecao}/${fichaId}/comentarios`, {
        method: 'POST',
        body: JSON.stringify(corpo),
      }),
    onSuccess: recarregar,
  });

  const resolver = useMutation({
    mutationFn: ({ id, resolvido }: { id: number; resolvido: boolean }) =>
      api<ComentarioDoTexto>(`/api/mundo/comentarios/${id}/resolucao`, {
        method: 'PATCH',
        body: JSON.stringify({ resolvido }),
      }),
    onSuccess: recarregar,
  });

  const excluir = useMutation({
    mutationFn: (id: number) => api<void>(`/api/mundo/comentarios/${id}`, { method: 'DELETE' }),
    onSuccess: recarregar,
  });

  return { criar, resolver, excluir };
}
